import { useState, useCallback } from 'react'
import { createFileRoute, useParams, Link } from '@tanstack/react-router'
import { Loader2, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useProjects } from '@/hooks/useProjects'
import { AuditWizard } from '@/components/audit/AuditWizard'
import { AuditProgress } from '@/components/audit/AuditProgress'
import { AuditDashboard } from '@/components/audit/AuditDashboard'
import { AuditHistory } from '@/components/audit/AuditHistory'
import { AuditComparison } from '@/components/audit/AuditComparison'
import { PageHeader } from '@/components/shared/PageHeader'
import { ArrowLeft } from 'lucide-react'

export const Route = createFileRoute('/_authenticated/projekty/$projectId/audyt')({
  component: AuditPage,
})

type ViewState = 'history' | 'wizard' | 'progress' | 'dashboard' | 'comparison'

function AuditPage() {
  const { projectId } = useParams({
    from: '/_authenticated/projekty/$projectId/audyt',
  })
  const { data: projects, isLoading } = useProjects()
  const project = projects?.find((p) => p.id === projectId)

  const [view, setView] = useState<ViewState>('history')
  const [auditId, setAuditId] = useState<string | null>(null)
  const [compareIds, setCompareIds] = useState<[string, string] | null>(null)

  const handleNewAudit = useCallback(() => {
    setAuditId(null)
    setCompareIds(null)
    setView('wizard')
  }, [])

  const handleAuditStarted = useCallback((id: string) => {
    setAuditId(id)
    setView('progress')
  }, [])

  const handleViewReport = useCallback((id: string) => {
    setAuditId(id)
    setView('dashboard')
  }, [])

  const handleSelectAudit = useCallback((id: string) => {
    setAuditId(id)
    setView('dashboard')
  }, [])

  const handleCompare = useCallback((idA: string, idB: string) => {
    setCompareIds([idA, idB])
    setView('comparison')
  }, [])

  const handleBackToHistory = useCallback(() => {
    setAuditId(null)
    setCompareIds(null)
    setView('history')
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--text-muted)' }} />
      </div>
    )
  }

  if (!project) {
    return (
      <div className="space-y-4">
        <p style={{ color: 'var(--text-muted)' }}>Nie znaleziono projektu.</p>
        <Button variant="outline" asChild>
          <Link to="/projekty">Wróć do projektów</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audyt SEO"
        breadcrumbItems={[
          { label: 'Projekty', to: '/projekty' },
          { label: project.name, to: '/projekty/$projectId', params: { projectId } },
          { label: 'Audyt SEO' },
        ]}
        actions={
          view !== 'wizard' ? (
            <Button onClick={handleNewAudit}>
              <Plus className="mr-2 h-4 w-4" />
              Nowy audyt
            </Button>
          ) : undefined
        }
      />

      {view === 'history' && (
        <AuditHistory
          projectId={projectId}
          onSelectAudit={handleSelectAudit}
          onCompare={handleCompare}
          onNewAudit={handleNewAudit}
        />
      )}

      {view === 'wizard' && (
        <AuditWizard
          projectId={projectId}
          projectUrl={project?.url}
          onAuditStarted={handleAuditStarted}
        />
      )}

      {view === 'progress' && auditId && (
        <AuditProgress
          auditId={auditId}
          projectId={projectId}
          onViewReport={handleViewReport}
          onRetry={handleNewAudit}
        />
      )}

      {view === 'dashboard' && auditId && (
        <div className="space-y-4">
          <Button variant="outline" size="sm" onClick={handleBackToHistory}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Historia audytów
          </Button>
          <AuditDashboard auditId={auditId} projectId={projectId} />
        </div>
      )}

      {view === 'comparison' && compareIds && (
        <AuditComparison
          auditIdA={compareIds[0]}
          auditIdB={compareIds[1]}
          onBack={handleBackToHistory}
        />
      )}
    </div>
  )
}
